import React, { useEffect, useState } from 'react';
import { withInject, errorBoundary, CopyToClipboard } from '@piwikpro/platform';
import { FormikProps } from 'formik';
import { TFunction } from 'react-i18next';
import { AuthSettings, SignatureValidations } from '@piwikpro/auth-crate';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import { FormValidator } from '@piwikpro/form-crate';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import {
  Card,
  Button,
  Text,
  Stack,
  withTranslation,
  CodeEditor,
  Spinner,
  SelectControl,
  ProgressStepper,
  Icon,
  Form,
  TextAreaControl,
  TextFieldControl,
  Divider,
  Information,
  Link,
} from '@piwikpro/ui-components';
import FailMessage from '../FailMessage';
import SamlDescription from './SamlDescription';
import SamlStatus from './SamlStatus';

/* eslint-disable camelcase */
interface ApiErrorFields {
  sign_on_url: string[]
  entity_id: string[]
  x509_certificate: string[]
}

const API_FIELDS_MAPPING = {
  sign_on_url: 'signOnUrl',
  entity_id: 'entityId',
  x509_certificate: 'publicCertificate',
};

interface FormValues {
  signOnUrl: string
  entityId: string
  publicCertificate: string
  signatureValidation: SignatureValidations
}

export interface ISamlProps {
  saml: any
  validation: FormValidator
  productAnalytics: ProductAnalytics
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
  isSamlAvailable: boolean
  isSamlVerified: boolean
  isSamlEnabled: boolean
  authSettings: AuthSettings
  location: Location
  signatureValidation: SignatureValidations
  metadata: string
  isLoading: boolean
  failed: boolean
  t: TFunction
}

const downloadXml = (data: string) => {
  const filename = 'piwik_pro_saml_metadata.xml';
  const anchor = document.createElement('a');
  const blobData = new Blob([data], { type: 'text/plain' });

  anchor.setAttribute('href', window.URL.createObjectURL(blobData));
  anchor.setAttribute('download', filename);

  anchor.click();
};

const setFormErrors = (errors: ApiErrorFields) => {
  const newObj = Object.fromEntries(
    Object.entries(errors).map(([k, v]) => [API_FIELDS_MAPPING[k as keyof ApiErrorFields], v[0]]),
  );
  return newObj;
};

const getCardSectionWrapper: React.FunctionComponent<React.ReactNode> = children => (
  <Card>
    <Card.Section>
      {children}
    </Card.Section>
  </Card>
);

export const Saml: React.FunctionComponent<ISamlProps> = ({
  isLoading,
  failed,
  isSamlAvailable,
  isSamlVerified,
  isSamlEnabled,
  authSettings,
  metadata,
  saml,
  validation,
  productAnalytics,
  UnsavedChangesTracker,
  location,
  t,
}) => {
  const params = new URLSearchParams(location.search);
  const samlTestError = params.get('error') === 'true';
  const [formExpanded, setFormExpanded] = useState(samlTestError);
  const handleSubmit = async (
    values: FormValues,
    api: FormikProps<FormValues>,
  ) => {
    const {
      signOnUrl, entityId, publicCertificate, signatureValidation,
    } = values;

    try {
      await authSettings.testSamlAuthentication(
        {
          signOnUrl,
          entityId,
          publicCertificate,
          signatureValidation,
        },
      );
      location.assign('/api/auth/v1/saml/sso');
    } catch (err: any) {
      if (Object.keys(err).includes('errors')) {
        api.setErrors(setFormErrors(err.errors.identity_provider));
      }
    } finally {
      api.setSubmitting(false);
    }
  };

  const handleEnable = async () => {
    authSettings.setSamlAuthentication({ enable: !saml.enabled }).then(
      () => authSettings.getSettingsForOrganization(),
    ).catch(() => {});
  };

  const handleDelete = async () => {
    authSettings.deleteSamlAuthentication().then(
      () => authSettings.getSettingsForOrganization(),
    ).catch(() => {});
  };

  const validationSchema = validation.createValidationSchema(validators => ({
    signOnUrl: validators.auth.signOnUrl,
    entityId: validators.auth.entityId,
    publicCertificate: validators.auth.publicCertificate,
  }));

  if (samlTestError) {
    productAnalytics.push({ type: '@platform/action/auth/samlTest/loginFlow/failed' });
  }

  useEffect(() => {
    if (!isSamlAvailable) {
      return;
    }
    authSettings.getSamlMetadata().catch(() => null);
  }, [isSamlAvailable]);

  useEffect(
    () => {
      if (isSamlVerified && !isSamlEnabled) {
        setFormExpanded(true);
      }
    }, [isSamlVerified, isSamlEnabled],
  );

  if (isLoading) {
    return getCardSectionWrapper(<Spinner />);
  }

  if (failed) {
    return getCardSectionWrapper(<FailMessage />);
  }

  return (
    <Card dataId="AccountSettingsSaml">
      <SamlDescription />
      {isSamlAvailable && (
        <SamlStatus
          formExpanded={formExpanded}
          setFormExpanded={setFormExpanded}
        />
      )}
      {isSamlAvailable && formExpanded && (
        <Form
          isInitialValid
          validateOnChange
          id="saml-setup-form"
          dataId="SamlSetupForm"
          initialValues={{
            signOnUrl: saml.identityProvider.sign_on_url,
            entityId: saml.identityProvider.entity_id,
            publicCertificate: saml.identityProvider.x509_certificate,
            signatureValidation: saml.serviceProvider.signatureValidation,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          render={(api: FormikProps<FormValues>) => {
            const formHasEmptyFields = Object.values(api.values).some(value => value === '');

            return (
              <>
                <Card.Section>
                  <Stack vertical>
                    <Stack.Item>
                      <Text as="p" weight={600} dataId="Label">{t('accountSecurity.saml-settings.metadata.title')}</Text>
                    </Stack.Item>
                    {!isSamlEnabled && (
                      <Stack.Item>
                        <Stack verticalAlignment="center" spacing="extraNarrow">
                          <Stack.Item>
                            <ProgressStepper.Step index={1} status="finished" appearance="label" />
                          </Stack.Item>
                          <Stack.Item fill>
                            <Text as="p">{t('accountSecurity.saml-settings.first-step.info')}</Text>
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                    )}
                    <Stack.Item fill>
                      <Stack vertical spacing="narrow">
                        <Stack.Item>
                          <CodeEditor
                            name="samlMetadata"
                            label={isSamlEnabled ? t('accountSecurity.saml-settings.first-step.metadata-label') : ''}
                            value={metadata}
                            mode="no-highlight"
                            readOnly
                            fullWidth
                          />
                        </Stack.Item>
                        <Stack.Item fill>
                          <Stack>
                            <Stack.Item fill>
                              <CopyToClipboard text={metadata} />
                            </Stack.Item>
                            <Stack.Item>
                              <Button
                                appearance="simple"
                                onClick={() => downloadXml(metadata)}
                                text={t('accountSecurity.saml-settings.first-step.download')}
                                icon="download"
                              />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                        {!isSamlEnabled && (
                          <Stack.Item previousSpacing="default">
                            <Stack verticalAlignment="center" spacing="extraNarrow">
                              <Stack.Item>
                                <ProgressStepper.Step index={2} status="finished" appearance="label" />
                              </Stack.Item>
                              <Stack.Item fill>
                                <Text as="p">{t('accountSecurity.saml-settings.metadata.signature-type.descritpion')}</Text>
                              </Stack.Item>
                            </Stack>
                          </Stack.Item>
                        )}
                        <Stack.Item previousSpacing="default" fill>
                          <SelectControl
                            dataId="SamlAssertionTypeSelect"
                            name="signatureValidation"
                            label={t('accountSecurity.saml-settings.metadata.signature-type.select.label')}
                            disabled={isSamlEnabled}
                            options={[{
                              id: 1,
                              name: '',
                              items: [
                                {
                                  id: 1,
                                  value: 'assertion',
                                  name: t('accountSecurity.saml-settings.metadata.signature-type.select.assertion'),
                                },
                                {
                                  id: 2,
                                  value: 'both',
                                  name: t('accountSecurity.saml-settings.metadata.signature-type.select.both'),
                                },
                                {
                                  id: 3,
                                  value: 'response',
                                  name: t('accountSecurity.saml-settings.metadata.signature-type.select.response'),
                                },
                              ],
                            }]}
                          />
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                  </Stack>
                </Card.Section>
                <Card.Section>
                  <Stack vertical>
                    <Stack.Item>
                      <Text as="p" weight={600} dataId="Label">{t('accountSecurity.saml-settings.setup.title')}</Text>
                    </Stack.Item>
                    {!saml.enabled && (
                      <Stack.Item>
                        <Stack verticalAlignment="center" spacing="extraNarrow">
                          <Stack.Item>
                            <ProgressStepper.Step index={3} status="finished" appearance="label" />
                          </Stack.Item>
                          <Stack.Item fill>
                            <Text as="p">{t('accountSecurity.saml-settings.second-step.info')}</Text>
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                    )}
                    <Stack.Item fill>

                      <Stack vertical>
                        <Stack.Item fill>
                          <TextFieldControl
                            disabled={saml.enabled}
                            name="signOnUrl"
                            label={t('accountSecurity.saml-settings.sing-on-url.title')}
                            info={t('accountSecurity.saml-settings.sing-on-url.info')}
                          />
                        </Stack.Item>
                        <Stack.Item fill>
                          <TextFieldControl
                            disabled={saml.enabled}
                            name="entityId"
                            label={t('accountSecurity.saml-settings.issuer.title')}
                            info={t('accountSecurity.saml-settings.issuer.info')}
                          />
                        </Stack.Item>
                        <Stack.Item fill>
                          <TextAreaControl
                            disabled={saml.enabled}
                            name="publicCertificate"
                            label={t('accountSecurity.saml-settings.public-certificate.title')}
                          />
                        </Stack.Item>

                        {!saml.enabled && (
                          <>
                            <Stack.Item>
                              <Stack verticalAlignment="center">
                                <Stack.Item>
                                  <UnsavedChangesTracker
                                    render={Api => (
                                      <>
                                        <Api.UnsavedChangesModal />
                                        <Button
                                          disabled={
                                            api.isSubmitting
                                          || formHasEmptyFields
                                          || !api.isValid
                                          }
                                          text={t('accountSecurity.saml-settings.test-button.title')}
                                          appearance="default"
                                          onClick={async () => {
                                            // resolves the issue with unwanted
                                            // native prompt in Safari ONLY
                                            Api.forceDisableNativePrompt();
                                            api.submitForm();
                                          }}
                                        />
                                      </>
                                    )}
                                    type="twoOptions"
                                  />
                                </Stack.Item>
                                <Stack.Item>
                                  <div style={{ marginTop: '6px' }}>
                                    <Stack spacing="narrow">
                                      <Stack.Item>
                                        <Icon name="info-circle" color="blue" />
                                      </Stack.Item>
                                      <Stack.Item>
                                        <Text as="p" size={12} color="InkLighter" dataId="Caption">{t('accountSecurity.saml-settings.test-button.info')}</Text>
                                      </Stack.Item>
                                    </Stack>
                                  </div>
                                </Stack.Item>
                              </Stack>
                            </Stack.Item>
                            <Stack.Item fill>
                              {
                                saml.verified && (
                                  <Information type="success">
                                    {
                                      t('accountSecurity.saml-settings.information.passed')
                                    }
                                  </Information>
                                )
                              }
                              {samlTestError && (
                                <Information type="error">
                                  {t('accountSecurity.saml-settings.information.failed')}
                                  {' '}
                                  <Link href="/api/auth/v1/saml/trace" size="small" target="_blank">
                                    {t('accountSecurity.saml-settings.information.details')}
                                  </Link>
                                </Information>
                              )}
                            </Stack.Item>
                          </>
                        )}
                        <Stack.Item fill previousSpacing={saml.verified || samlTestError ? 'wide' : 'narrow'}>
                          <Divider noMargin />
                        </Stack.Item>
                        <Stack.Item />
                      </Stack>
                      <Stack>
                        <Stack.Item fill>
                          <Button
                            appearance={saml.enabled ? 'default' : 'primary'}
                            text={
                              t(saml.enabled
                                ? 'accountSecurity.saml-settings.turn-off-button'
                                : 'accountSecurity.saml-settings.turn-on-button')
                            }
                            onClick={() => handleEnable()}
                            disabled={!saml.verified}
                          />
                        </Stack.Item>
                        <Stack.Item>
                          {saml.enabled && (
                            <Button
                              appearance="destructive-simple"
                              text={t('accountSecurity.saml-settings.delete-button')}
                              onClick={() => handleDelete()}
                            />
                          )}
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                  </Stack>
                </Card.Section>
              </>
            );
          }}
        />
      )}
    </Card>
  );
};

export default withInject({
  validation: 'FormCrate.validation',
  authSettings: 'AuthCrate.authSettings',
  productAnalytics: 'productAnalytics',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
  location: 'location',
})(
  withTranslation(['administration'])(errorBoundary(Saml)),
);
